import {Action, Card, useDialog} from 'platform/components';
import {Box} from 'platform/foundation';

import {useCallback} from 'react';
import {useDispatch} from 'react-redux';

import {mergeAll} from 'ramda';

import {documentContextApi} from '@dms/api/core';
import {
  DOCUMENT_ACTION_COLUMNS,
  useGetDocumentDatagridActions,
} from '@dms/features/document-context';
import i18n from '@dms/i18n';
import {
  DmsDataGrid,
  type DmsDataGridProps,
  refreshDmsDatagrid,
  Section,
  UploadDocumentsDialog,
  useCustomerUrl,
} from '@dms/shared';

import {buildArray} from 'shared/web';

import type {QueryFilterObject} from 'features/eag-grid';

export function Documents(props: Pick<DmsDataGridProps, 'maximization'>) {
  const {customerId} = useCustomerUrl();
  const [isUploadDialogOpen, openUploadDialog, closeUploadDialog] = useDialog();
  const dispatch = useDispatch();

  const refreshDataGrid = () => refreshDmsDatagrid('document-context-list-customer');

  const queryModifier = useCallback(
    (filter: QueryFilterObject) => mergeAll([filter, {customerId}]),
    [customerId]
  );

  const [actionCallback, bulkSignatureAction] = useGetDocumentDatagridActions({
    contextId: customerId,
    contextTarget: 'customer',
    refreshData: refreshDataGrid,
    customerId,
  });

  const onDocumentCreated = () => {
    refreshDataGrid();

    dispatch(
      documentContextApi.util.invalidateTags([{type: 'documentsCount', id: customerId ?? ''}])
    );
  };

  return (
    <Section>
      <Card
        title={i18n.t('page.customer.documents.title')}
        actions={buildArray<Action>([
          {
            variant: 'link',
            type: 'button',
            leftIcon: 'action/backup',
            onClick: openUploadDialog,
            title: i18n.t('entity.document.actions.uploadDocument'),
          },
        ]).add(...bulkSignatureAction)}
      >
        <Box height={130} minHeight={50} position="relative">
          <DmsDataGrid
            maximization={props.maximization}
            data-testid="customer-documents"
            gridCode="document-context-list-customer"
            emptyState={{
              headline: i18n.t('page.customer.notifications.noDocuments'),
              subheadline: i18n.t('page.customer.notifications.documentListWillAppearHere'),
            }}
            actionCallback={actionCallback}
            predefinedColumns={DOCUMENT_ACTION_COLUMNS}
            queryModifier={queryModifier}
          />
        </Box>
      </Card>
      <UploadDocumentsDialog
        isOpen={isUploadDialogOpen}
        handleClose={closeUploadDialog}
        onDocumentCreated={onDocumentCreated}
        contextTarget="customer"
        contextId={customerId}
      />
    </Section>
  );
}
